import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Link, type LinkProps } from "react-router";
import { LoaderCircle, type LucideIcon } from "lucide-react";

import { Icon } from "./Icon";

/**
 * Four variants, two sizes — the only buttons of the app.
 *
 *   primary    the one action the page exists for (Save, Create, Deploy)
 *   secondary  every other action; `Cancel` is ALWAYS secondary
 *   ghost      a quiet action next to text or inside a row
 *   danger     SOLID red: the confirm button of a destructive action. A row's
 *              destructive gesture is not a red button but an entry of its
 *              menu (`KebabMenu`, `danger`) that opens the confirmation.
 *
 * A word on classes. Each element gets ONE class per property — colour, height,
 * padding — because two competing utilities are decided by the order of the
 * generated stylesheet, not by the order written here. So `className` is for
 * LAYOUT only (margin, alignment, `w-full`): never a colour, a text size or a
 * padding — the variant and the size own them.
 *
 * `type` defaults to "button", NOT to the browser's "submit": a button in a
 * form that was written without a type submits it, and that has to be asked
 * for on purpose (`type="submit"`).
 */
export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "md" | "sm";

const BASE =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border text-sm font-medium transition-colors";

// Two literal tables, not one with a computed hover prefix: Tailwind finds a
// class by reading its full text in the source. A <button> can be `:disabled`
// (so its hover is `enabled:hover:`); a link cannot, and `:enabled` never
// matches an <a>. KEEP THEM IN STEP: they say the same thing twice.
//
// A ghost button's hover fill is `sunken-deep`, not `sunken`: it lives on the
// page, on a card AND on the shell's chrome (which IS `sunken`), and one step
// further down than the chrome is the only fill that shows on all three.
const VARIANT = {
  primary:
    "border-brand-500 bg-brand-500 text-on-fill enabled:hover:border-brand-600 enabled:hover:bg-brand-600",
  secondary: "border-line-field bg-surface text-ink enabled:hover:bg-sunken",
  ghost:
    "border-transparent bg-transparent text-ink-muted enabled:hover:bg-sunken-deep enabled:hover:text-ink",
  danger:
    "border-danger-fg bg-danger-fg text-on-fill enabled:hover:border-danger-deep enabled:hover:bg-danger-deep",
} as const satisfies Record<ButtonVariant, string>;

// No `danger` here: a link that navigates is not a destructive gesture.
const LINK_VARIANT = {
  primary:
    "border-brand-500 bg-brand-500 text-on-fill hover:border-brand-600 hover:bg-brand-600",
  secondary: "border-line-field bg-surface text-ink hover:bg-sunken",
  ghost:
    "border-transparent bg-transparent text-ink-muted hover:bg-sunken-deep hover:text-ink",
} as const satisfies Record<Exclude<ButtonVariant, "danger">, string>;

const SIZE = { md: "h-9 px-3.5", sm: "h-7 px-2.5" } as const satisfies Record<ButtonSize, string>;
const SQUARE = { md: "h-9 w-9", sm: "h-7 w-7" } as const satisfies Record<ButtonSize, string>;

// A loading button is disabled (no second click) but must not look disabled: it
// is working. So it gets its own cursor and keeps its full colour.
const IDLE = "disabled:cursor-not-allowed disabled:opacity-50";
const BUSY = "cursor-progress";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** An icon before the label. */
  icon?: LucideIcon;
  /**
   * The action is running: disables the button, shows a spinner in place of the
   * icon and sets `aria-busy`. The label is the caller's ("Saving…").
   */
  loading?: boolean;
  /** Layout only — see the note at the top of this file. */
  className?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    icon,
    loading = false,
    type = "button",
    disabled,
    className = "",
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${BASE} ${VARIANT[variant]} ${SIZE[size]} ${loading ? BUSY : IDLE} ${className}`}
    >
      {loading ? (
        <Icon icon={LoaderCircle} className="animate-spin" />
      ) : (
        icon && <Icon icon={icon} />
      )}
      {children}
    </button>
  );
});

export interface ButtonLinkProps extends Omit<LinkProps, "className"> {
  variant?: Exclude<ButtonVariant, "danger">;
  size?: ButtonSize;
  icon?: LucideIcon;
  className?: string;
}

/** A link to another page that looks like a `Button`: navigation, not an action. */
export function ButtonLink({
  variant = "secondary",
  size = "md",
  icon,
  className = "",
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link
      {...rest}
      className={`${BASE} ${LINK_VARIANT[variant]} ${SIZE[size]} ${className}`}
    >
      {icon && <Icon icon={icon} />}
      {children}
    </Link>
  );
}

export interface IconButtonProps
  extends Omit<ButtonProps, "children" | "icon" | "aria-label" | "variant"> {
  icon: LucideIcon;
  /**
   * What the button DOES ("Collapse task panel"). It is the accessible name —
   * an icon alone names nothing — and, unless `title` says otherwise, the
   * tooltip a mouse user reads.
   */
  label: string;
  /** No `primary`, no `danger`: an icon alone is never THE action, nor a confirmation. */
  variant?: Extract<ButtonVariant, "secondary" | "ghost">;
}

/** A square button whose only content is an icon. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon,
      label,
      variant = "ghost",
      size = "md",
      loading = false,
      type = "button",
      disabled,
      title,
      className = "",
      ...rest
    },
    ref,
  ) {
    return (
      <button
        {...rest}
        ref={ref}
        type={type}
        aria-label={label}
        title={title ?? label}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={`${BASE} ${VARIANT[variant]} ${SQUARE[size]} ${loading ? BUSY : IDLE} ${className}`}
      >
        <Icon icon={loading ? LoaderCircle : icon} className={loading ? "animate-spin" : ""} />
      </button>
    );
  },
);
